import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users, UserCheck, ClipboardCheck, FileCheck, Crown, TrendingUp,
  CalendarDays, Activity, RefreshCw, ArrowLeft, Download, Smartphone,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'all', label: 'All time' },
];

const STAGE_ICONS = {
  signups: Users,
  verified: UserCheck,
  profile_complete: ClipboardCheck,
  terms_accepted: FileCheck,
  trial_active: Activity,
  paid: Crown,
};

const AdminFunnelDashboard = ({ onBack }) => {
  const [range, setRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [pwaStats, setPwaStats] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchAll = async (rangeKey = range) => {
    setLoading(true);
    setError(null);
    try {
      const [ov, fn, tl, pwa] = await Promise.all([
        axios.get(`${API}/admin/overview`, { headers: authHeaders }),
        axios.get(`${API}/admin/funnel?range=${rangeKey}`, { headers: authHeaders }),
        axios.get(`${API}/admin/signups-timeline?days=30`, { headers: authHeaders }),
        axios.get(`${API}/admin/pwa-stats?range=${rangeKey}`, { headers: authHeaders }),
      ]);
      setOverview(ov.data);
      setFunnel(fn.data);
      setTimeline(tl.data.timeline || []);
      setPwaStats(pwa.data);
    } catch (err) {
      const msg = err.response?.status === 403
        ? 'You do not have admin access to view this page.'
        : 'Failed to load analytics. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(range); /* eslint-disable-next-line */ }, [range]);

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-300">Loading analytics…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg text-center">
          <div className="text-red-500 font-semibold mb-2">Access denied</div>
          <p className="text-gray-600 dark:text-gray-300 text-sm">{error}</p>
          {onBack && (
            <button
              onClick={onBack}
              className="mt-5 px-4 py-2 bg-[#FF6600] text-white rounded-lg font-medium hover:bg-[#e65c00]"
              data-testid="admin-back-btn"
            >
              Back
            </button>
          )}
        </div>
      </div>
    );
  }

  const maxCount = funnel ? Math.max(...funnel.stages.map(s => s.count), 1) : 1;
  const maxTimeline = Math.max(...timeline.map(t => t.signups), 1);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-16" data-testid="admin-funnel-dashboard">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-testid="admin-back-btn-header">
                <ArrowLeft size={20} className="text-gray-700 dark:text-gray-200" />
              </button>
            )}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Admin · Conversion Funnel</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Track signups, profiles and subscriptions</p>
            </div>
          </div>
          <button
            onClick={() => fetchAll(range)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            data-testid="admin-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw size={18} className={`text-gray-600 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Top counters (All time) */}
        {overview && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total users"        value={overview.total_users}        icon={Users}       color="#FF6600" />
            <StatCard label="Profile completed"  value={overview.total_profile_complete} icon={ClipboardCheck} color="#2563eb" />
            <StatCard label="Active trials"      value={overview.total_trials}       icon={Activity}    color="#f59e0b" />
            <StatCard label="Paying subscribers" value={overview.total_paying}       icon={Crown}       color="#10b981" />
          </div>
        )}

        {/* Today / Weekly */}
        {overview && (
          <div className="grid grid-cols-2 gap-3">
            <SmallStat label="Signups today" value={overview.signups_today} />
            <SmallStat label="Signups last 7 days" value={overview.signups_last_7_days} />
          </div>
        )}

        {/* Range selector */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays size={18} className="text-gray-600 dark:text-gray-300" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Funnel range</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.key}
                data-testid={`range-${opt.key}-btn`}
                onClick={() => setRange(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  range === opt.key
                    ? 'bg-[#FF6600] text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Conversion funnel */}
        {funnel && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <TrendingUp size={20} className="text-[#FF6600]" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Conversion Funnel</h2>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">Overall conversion</div>
                <div className="text-xl font-bold text-green-600">{funnel.overall_conversion_pct}%</div>
              </div>
            </div>

            <div className="space-y-3">
              {funnel.stages.map((s, idx) => {
                const Icon = STAGE_ICONS[s.key] || Users;
                const barWidth = Math.max((s.count / maxCount) * 100, 2);
                return (
                  <div key={s.key} data-testid={`funnel-stage-${s.key}`} className="group">
                    <div className="flex items-center gap-3 mb-1.5">
                      <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                        <Icon size={18} className="text-[#FF6600]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {s.label}
                          </span>
                          <span className="text-sm font-bold text-gray-900 dark:text-white">
                            {s.count.toLocaleString()}
                          </span>
                        </div>
                        {idx > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {s.of_prev}% from previous step
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-12 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#FF6600] to-[#FF8533] rounded-full transition-all duration-500"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {funnel.cancelled > 0 && (
              <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Cancelled / churned</span>
                <span className="font-semibold text-red-500">{funnel.cancelled}</span>
              </div>
            )}
          </div>
        )}

        {/* PWA Install Funnel */}
        {pwaStats && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700" data-testid="pwa-stats-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Smartphone size={20} className="text-[#FF6600]" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">PWA Install Funnel</h2>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">Install rate</div>
                <div className="text-xl font-bold text-green-600">{pwaStats.install_conversion_pct}%</div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <PwaStat label="Prompts shown"  value={pwaStats.prompt_shown + pwaStats.ios_hint_shown} icon={Smartphone} />
              <PwaStat label="Install clicked" value={pwaStats.install_clicked}    icon={Download} />
              <PwaStat label="Installed"       value={pwaStats.install_completed}  icon={Crown}     highlight />
              <PwaStat label="Dismissed"       value={pwaStats.install_dismissed}  icon={Activity} />
            </div>

            {Object.keys(pwaStats.by_platform || {}).length > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Installs by platform
                </div>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(pwaStats.by_platform).map(([p, c]) => (
                    <span key={p} className="px-3 py-1 rounded-full bg-orange-100 dark:bg-orange-900/30 text-[#FF6600] text-xs font-semibold capitalize">
                      {p}: {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Daily signups timeline */}
        {timeline.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Signups — Last 30 days</h2>
            <div className="flex items-end gap-1 h-32">
              {timeline.map(t => (
                <div
                  key={t.date}
                  className="flex-1 bg-gradient-to-t from-[#FF6600] to-[#FF8533] rounded-t-sm min-h-[2px] relative group"
                  style={{ height: `${(t.signups / maxTimeline) * 100}%` }}
                  title={`${t.date}: ${t.signups} signups`}
                >
                  <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-gray-600 dark:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.signups}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-gray-500 dark:text-gray-400">
              <span>{timeline[0]?.date.slice(5)}</span>
              <span>{timeline[timeline.length - 1]?.date.slice(5)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{(value || 0).toLocaleString()}</div>
  </div>
);

const SmallStat = ({ label, value }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700 flex items-baseline justify-between">
    <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-lg font-bold text-[#FF6600]">{(value || 0).toLocaleString()}</span>
  </div>
);

const PwaStat = ({ label, value, icon: Icon, highlight = false }) => (
  <div className={`rounded-xl p-3 border ${highlight
    ? 'bg-gradient-to-br from-[#FF6600] to-[#FF8533] border-transparent text-white'
    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white'
  }`}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon size={14} className={highlight ? 'text-white/90' : 'text-[#FF6600]'} />
      <span className={`text-[11px] font-medium uppercase tracking-wide ${highlight ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}`}>{label}</span>
    </div>
    <div className="text-xl font-bold">{(value || 0).toLocaleString()}</div>
  </div>
);

export default AdminFunnelDashboard;
